import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { t } from '../i18n';
import { FileText } from 'lucide-react';
import DocumentDetailModal from '../components/DocumentDetailModal';

export default function DocumentsPage() {
  const { lang } = useAuth();
  const [docs, setDocs] = useState([]);
  const [selId, setSelId] = useState(null);
  const [busy, setBusy] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/documents`).then(r => { setDocs(r.data); setBusy(false); }).catch(() => setBusy(false));
  }, []);

  const filtered = filter === 'all' ? docs : docs.filter(d => d.department === filter);

  return (
    <div className="fade-up" data-testid="documents-page">
      <div className="data-label accent-line text-[#DDA079] mb-3">{t(lang, 'nav.documents')}</div>
      <h1 className="font-headings text-4xl text-white mb-2">Document Library</h1>
      <p className="text-slate-400 mb-8">Only documents you are authorised to view are shown.</p>

      <div className="flex gap-2 mb-6">
        {['all', 'HR', 'Procurement', 'Finance'].map(f => {
          const count = f === 'all' ? docs.length : docs.filter(d => d.department === f).length;
          if (count === 0 && f !== 'all') return null;
          return (
            <button
              key={f}
              data-testid={`docfilter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-[12px] font-mono uppercase tracking-wider border rounded-sm ${filter === f ? 'border-[#C17F59] text-white bg-[#C17F59]/10' : 'border-white/10 text-slate-400 hover:text-white'}`}
            >
              {f} · {count}
            </button>
          );
        })}
      </div>

      {busy ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="skeleton h-16 rounded-sm" />)}</div>
      ) : (
        <div className="grid gap-2">
          {filtered.map(d => (
            <button
              key={d.id}
              data-testid={`doc-${d.id}`}
              onClick={() => setSelId(d.id)}
              className="v-card v-card-hover p-4 text-left flex items-start justify-between gap-4"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <FileText size={14} className="text-[#C17F59]" />
                  <span className="text-white text-[14px]">{d.name}</span>
                  <span className="v-badge cost">{d.type}</span>
                  <span className="v-badge cost">{d.department}</span>
                  {d.language !== 'en' && <span className="v-badge conf">{d.language.toUpperCase()}</span>}
                </div>
                <p className="text-slate-400 text-[13px] line-clamp-2 mt-2">{d.summary}</p>
                <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-500 font-mono">
                  <span>v{d.version}</span>
                  <span>{d.pages} pages</span>
                  {d.metadata?.vendor && <span>· {d.metadata.vendor}</span>}
                  {d.metadata?.expiry_date && <span>· expires {d.metadata.expiry_date}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {selId && <DocumentDetailModal docId={selId} onClose={() => setSelId(null)} />}
    </div>
  );
}
